import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Search, RefreshCw, AlertTriangle, ArrowDownToLine } from "lucide-react";
import { toast } from "sonner";

const cls = (n) => n === 0 ? "text-gray-500" : (n > 0 ? "text-emerald-600 font-semibold" : "text-red-600 font-semibold");

export default function ReconciliationPage() {
  const [rows, setRows] = useState([]);
  const [generatedAt, setGeneratedAt] = useState("");
  const [q, setQ] = useState("");
  const [onlyIssues, setOnlyIssues] = useState(false);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/stock-reconciliation");
      setRows(data.rows || []); setGeneratedAt(data.generated_at || "");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const needle = q.toLowerCase().trim();
    return rows.filter((r) => {
      if (onlyIssues && r.received === 0 && r.wh_transferred_out === 0 && r.ep_on_hand === 0) return false;
      if (onlyIssues && r.variance === 0) return false;
      if (!needle) return true;
      return r.name?.toLowerCase().includes(needle) || r.sku?.toLowerCase().includes(needle);
    });
  }, [rows, q, onlyIssues]);

  // KPIs
  const totals = useMemo(() => rows.reduce((acc, r) => {
    acc.received += r.received;
    acc.warehouse += r.warehouse_on_hand;
    acc.ep += r.ep_on_hand;
    acc.wh_out += r.wh_transferred_out;
    acc.sold += r.ep_sold_to_clients;
    if (r.variance !== 0) acc.withVariance += 1;
    return acc;
  }, { received: 0, warehouse: 0, ep: 0, wh_out: 0, sold: 0, withVariance: 0 }), [rows]);

  const exportCsv = () => {
    const headers = ["SKU", "Name", "Received (POs)", "Warehouse on-hand", "WH transferred out", "DP on-hand", "DP sold to clients", "Variance"];
    const escape = (v) => /[",\n]/.test(String(v)) ? `"${String(v).replace(/"/g, '""')}"` : String(v);
    const body = filtered.map(r => [r.sku, r.name, r.received, r.warehouse_on_hand, r.wh_transferred_out, r.ep_on_hand, r.ep_sold_to_clients, r.variance].map(escape).join(",")).join("\n");
    const blob = new Blob([headers.join(",") + "\n" + body], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = "stock_reconciliation.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="p-3 sm:p-6 lg:p-10 max-w-full" data-testid="reconciliation-page">
      <header className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <p className="label-eyebrow">Supply chain integrity</p>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight mt-1">Stock Reconciliation</h1>
          <p className="text-gray-500 text-sm mt-1">Per-SKU flow: what came in from vendors, what's on hand, and where the gaps are.</p>
          {generatedAt && <p className="text-xs text-gray-400 mt-1">Generated {generatedAt.slice(0, 19).replace("T", " ")}</p>}
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" onClick={exportCsv} data-testid="export-recon-csv"><ArrowDownToLine size={14} className="mr-1" /> Export CSV</Button>
          <Button variant="outline" onClick={load} disabled={loading} data-testid="refresh-recon"><RefreshCw size={14} className={`mr-1 ${loading ? "animate-spin" : ""}`} /> Refresh</Button>
        </div>
      </header>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
        <Card className="p-4"><div className="label-eyebrow">Received (POs)</div><div className="text-2xl font-bold text-emerald-700">{totals.received}</div></Card>
        <Card className="p-4"><div className="label-eyebrow">Warehouse on-hand</div><div className="text-2xl font-bold">{totals.warehouse}</div></Card>
        <Card className="p-4"><div className="label-eyebrow">Transferred to DPs</div><div className="text-2xl font-bold">{totals.wh_out}</div></Card>
        <Card className="p-4"><div className="label-eyebrow">DP on-hand</div><div className="text-2xl font-bold">{totals.ep}</div></Card>
        <Card className={`p-4 ${totals.withVariance > 0 ? "bg-amber-50 border-amber-200" : ""}`}>
          <div className="label-eyebrow text-amber-700">SKUs w/ variance</div>
          <div className={`text-2xl font-bold ${totals.withVariance > 0 ? "text-amber-700" : ""}`}>{totals.withVariance}</div>
        </Card>
      </div>

      {/* Explainer */}
      <Card className="p-3 mb-4 bg-blue-50 border-blue-200 text-xs text-blue-900" data-testid="variance-explainer">
        <strong>How to read this:</strong> Variance = <em>(warehouse on-hand + DP on-hand) − (received via POs − sold to clients)</em>.
        A positive variance means physical stock exceeds tracked inflows — usually because products existed <em>before</em> movement logging started (that's your "opening" stock).
        A <span className="text-red-600 font-semibold">negative</span> variance is worth investigating — potential shrinkage, unlogged transfers, or over-fulfilled orders.
      </Card>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-3 items-center">
        <div className="relative flex-1 min-w-64">
          <Search size={14} className="absolute left-3 top-2.5 text-gray-400" />
          <Input className="pl-8" placeholder="Search name or SKU…" value={q} onChange={e => setQ(e.target.value)} data-testid="recon-search" />
        </div>
        <label className="inline-flex items-center gap-2 text-sm cursor-pointer" data-testid="only-issues">
          <input type="checkbox" checked={onlyIssues} onChange={e => setOnlyIssues(e.target.checked)} />
          Only SKUs with movement or variance
        </label>
      </div>

      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[var(--bg-soft)] text-xs uppercase tracking-wide text-gray-500">
              <tr>
                <th className="text-left p-3">Product</th>
                <th className="text-left p-3">SKU</th>
                <th className="text-right p-3" title="Total received from vendors via PO">Received</th>
                <th className="text-right p-3">WH on-hand</th>
                <th className="text-right p-3">WH → DP</th>
                <th className="text-right p-3">DP on-hand</th>
                <th className="text-right p-3">DP → clients</th>
                <th className="text-right p-3">Variance</th>
              </tr>
            </thead>
            <tbody>
              {filtered.slice(0, 500).map((r) => (
                <tr key={r.product_id} className={`border-t border-[var(--border)] hover:bg-gray-50 ${r.variance < 0 ? "bg-red-50/30" : ""}`} data-testid={`recon-row-${r.sku}`}>
                  <td className="p-3 font-medium capitalize">{r.name}</td>
                  <td className="p-3 font-mono text-xs text-gray-500">{r.sku || "—"}</td>
                  <td className="p-3 text-right tabular-nums">{r.received}</td>
                  <td className="p-3 text-right tabular-nums">{r.warehouse_on_hand}</td>
                  <td className="p-3 text-right tabular-nums text-gray-600">{r.wh_transferred_out}</td>
                  <td className="p-3 text-right tabular-nums">{r.ep_on_hand}</td>
                  <td className="p-3 text-right tabular-nums text-gray-600">{r.ep_sold_to_clients}</td>
                  <td className="p-3 text-right tabular-nums">
                    <span className={cls(r.variance)}>
                      {r.variance < 0 && <AlertTriangle size={12} className="inline mr-1" />}
                      {r.variance > 0 ? "+" : ""}{r.variance}
                    </span>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={8} className="p-10 text-center text-gray-400">
                  {loading ? "Loading…" : "No SKUs match."}
                </td></tr>
              )}
            </tbody>
          </table>
          {filtered.length > 500 && <div className="p-3 border-t border-[var(--border)] text-xs text-gray-500">Showing first 500 of {filtered.length} rows. Refine with search.</div>}
        </div>
      </Card>
    </div>
  );
}
